export type ConsentPreferences = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  version: '2026-07-03';
  updatedAt: string;
};

const STORAGE_KEY = 'propickleball_cookie_consent_v1';
export const COOKIE_PREFERENCES_OPEN_EVENT = 'propickleball:open-cookie-preferences';

export type ConsentSaveResult =
  | { ok: true; preferences: ConsentPreferences }
  | { ok: false; reason: 'storage_unavailable' };

type CookiePreferencesOpenDetail = {
  trigger?: HTMLElement | null;
};

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
    gtag?: (...args: unknown[]) => void;
  }
}

const GTM_SCRIPT_ID = 'propickleball-gtm';
const GTM_CONTAINER_ID = 'GTM-54KK27BX';
const GTM_SERVING_URL = `https://propickleball.nl/metrics/kSclYIYswQi/?id=${GTM_CONTAINER_ID}`;

function loadGtm(): void {
  if (document.getElementById(GTM_SCRIPT_ID)) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
    brand_alias: 'propickleball',
    tenant_id: 'propickleball',
    event_source: 'browser'
  });
  const script = document.createElement('script');
  script.id = GTM_SCRIPT_ID;
  script.async = true;
  script.src = GTM_SERVING_URL;
  document.head.appendChild(script);
}

export function defaultConsentPreferences(): ConsentPreferences {
  return {
    necessary: true,
    analytics: false,
    marketing: false,
    version: '2026-07-03',
    updatedAt: new Date().toISOString()
  };
}

export function readConsentPreferences(): ConsentPreferences | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentPreferences>;
    if (parsed.version !== '2026-07-03') return null;
    return {
      necessary: true,
      analytics: parsed.analytics === true,
      marketing: parsed.marketing === true,
      version: '2026-07-03',
      updatedAt: typeof parsed.updatedAt === 'string' ? parsed.updatedAt : new Date().toISOString()
    };
  } catch {
    return null;
  }
}

export function saveConsentPreferences(
  preferences: Omit<ConsentPreferences, 'necessary' | 'version' | 'updatedAt'>
): ConsentSaveResult {
  const stored: ConsentPreferences = {
    necessary: true,
    analytics: preferences.analytics,
    marketing: preferences.marketing,
    version: '2026-07-03',
    updatedAt: new Date().toISOString()
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    return { ok: false, reason: 'storage_unavailable' };
  }
  applyConsentPreferences(stored);
  return { ok: true, preferences: stored };
}

export function requestCookiePreferences(trigger?: HTMLElement | null): void {
  window.dispatchEvent(new CustomEvent<CookiePreferencesOpenDetail>(COOKIE_PREFERENCES_OPEN_EVENT, {
    detail: { trigger }
  }));
}

export function subscribeToCookiePreferenceOpens(
  listener: (event: CustomEvent<CookiePreferencesOpenDetail>) => void
): () => void {
  const handler = (event: Event) => listener(event as CustomEvent<CookiePreferencesOpenDetail>);
  window.addEventListener(COOKIE_PREFERENCES_OPEN_EVENT, handler);
  return () => window.removeEventListener(COOKIE_PREFERENCES_OPEN_EVENT, handler);
}

export function applyConsentPreferences(preferences: ConsentPreferences): void {
  window.dataLayer = window.dataLayer || [];
  const analyticsStorage = preferences.analytics ? 'granted' : 'denied';
  const marketingStorage = preferences.marketing ? 'granted' : 'denied';

  window.dataLayer.push({
    event: 'consent_update',
    consent_provider: 'first_party_cmp',
    consent_analytics: analyticsStorage,
    consent_marketing: marketingStorage,
    consent_version: preferences.version
  });

  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: analyticsStorage,
      ad_storage: marketingStorage,
      ad_user_data: marketingStorage,
      ad_personalization: marketingStorage
    });
  }
  if (preferences.analytics || preferences.marketing) loadGtm();
}
